import React, { useEffect, useState } from "react";
import axios from "axios";
import Loader from "../../../components/Loader/Loader";
import useFocusTrap from "../../../hooks/useFocusTrap";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import Pill from "../../../components/v3/Pill";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { bdcApi } from "../../../services/bdcApi";

// status enum from migration 20260508000001 / sentMailCronJob.js
const STATUS_LABELS = {
  0: "Queued",
  1: "Sent",
  2: "Failed",
  3: "Dead-letter",
};

function StatusPill({ status }) {
  if (status === 1) return <Pill tone="mint">Sent</Pill>;
  if (status === 0) return <Pill tone="amber">Queued</Pill>;
  // Pair `s-fail` with `tbl-status` (the repo convention, e.g. ClaimQueue/
  // SLABreachMonitor) so failed rows get the same pill shape/padding/dot.
  if (status === 2) return <span className="tbl-status s-fail">Failed</span>;
  if (status === 3) return <span className="tbl-status s-fail">Dead-letter</span>;
  return <Pill tone="gray">—</Pill>;
}

function EmailLogViewer() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [toFilter, setToFilter] = useState("");
  const [emailType, setEmailType] = useState("");
  const [from, setFrom] = useState("");
  const [toDate, setToDate] = useState("");
  // `appliedFilters` is the snapshot the list effect reads; the individual
  // states above are just the form inputs. Apply/Reset publish a fresh object
  // so the effect re-runs (Apply always refetches); `refreshKey` lets resend
  // force a refetch without touching filters/page.
  const [appliedFilters, setAppliedFilters] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  const [openRowId, setOpenRowId] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const closeModal = () => {
    setOpenRow(null);
    setOpenRowId(null);
    setModalError(null);
  };
  const dialogRef = useFocusTrap({ active: !!openRow, onClose: closeModal });

  // List fetch — driven by [page, appliedFilters, refreshKey]. ignore-flag +
  // AbortController discard/cancel out-of-order responses (react.dev
  // "Synchronizing with Effects").
  useEffect(() => {
    const controller = new AbortController();
    let ignore = false;
    setLoading(true);
    const params = { page, limit: 50 };
    if (appliedFilters.status) params.status = appliedFilters.status;
    if (appliedFilters.to) params.to = appliedFilters.to;
    if (appliedFilters.emailType) params.emailType = appliedFilters.emailType;
    if (appliedFilters.from) params.from = appliedFilters.from;
    if (appliedFilters.toDate) params.toDate = appliedFilters.toDate;
    bdcApi
      .adListEmailLog(params, { signal: controller.signal })
      .then((res) => {
        if (ignore) return;
        const d = res?.data || {};
        setRows(Array.isArray(d.rows) ? d.rows : []);
        setTotal(d.total || 0);
        setTotalPages(d.total_pages || 0);
        setError(null);
      })
      .catch((err) => {
        if (ignore || axios.isCancel(err)) return;
        setError(err?.response?.data?.message || err.message || "Fetch failed");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [page, appliedFilters, refreshKey]);

  // Detail fetch — keyed on the selected row id. The ignore flag + abort ensure
  // a slower response for a previously-clicked row can't overwrite a newer
  // selection; on error the modal stays open with an inline message.
  useEffect(() => {
    if (openRowId == null) return undefined;
    const controller = new AbortController();
    let ignore = false;
    setOpenLoading(true);
    bdcApi
      .adGetEmailLogFull(openRowId, { signal: controller.signal })
      .then((res) => {
        if (!ignore) setOpenRow(res?.data || null);
      })
      .catch((err) => {
        if (ignore || axios.isCancel(err)) return;
        setModalError(err?.response?.data?.message || err.message || "Failed to load full row");
      })
      .finally(() => {
        if (!ignore) setOpenLoading(false);
      });
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [openRowId]);

  function applyFilters(e) {
    e.preventDefault();
    setPage(1);
    setAppliedFilters({ status, to: toFilter, emailType, from, toDate });
  }

  function resetFilters() {
    setStatus("");
    setToFilter("");
    setEmailType("");
    setFrom("");
    setToDate("");
    setPage(1);
    setAppliedFilters({});
  }

  function openFull(row) {
    setModalError(null);
    setOpenRow({ ...row, body: null }); // show partial immediately
    setOpenRowId(row.id); // triggers the detail-fetch effect
  }

  async function resend(row) {
    if (!window.confirm(`Re-queue email #${row.id} to ${row.to}?`)) return;
    setResendingId(row.id);
    try {
      await bdcApi.adResendEmail(row.id);
      setFlash({ tone: "info", text: `Email #${row.id} re-queued. Cron picks up within ~1 minute.` });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const code = err?.response?.status;
      const reason = err?.response?.data?.message;
      // The API returns TWO different 409s and they need opposite responses.
      // Branching on the HTTP status alone reported both as "already queued",
      // which is actively misleading for the second one: the operator is told
      // the mail is on its way, next to a banner saying cron picks it up within
      // a minute, and then waits for a send that can never happen.
      //
      //   already_queued                 harmless - the row is already status 0
      //   body_scrubbed_re_mint_required the invite token was scrubbed when the
      //                                  row went terminal (co-ownership review
      //                                  F3). Re-queuing it WOULD "succeed" and
      //                                  send a BLANK email. The only remedy is
      //                                  to re-mint the invite.
      let msg;
      let tone = "danger";
      if (code === 409 && reason === "body_scrubbed_re_mint_required") {
        msg =
          `Email #${row.id} cannot be resent: this director invite's link was ` +
          `removed for security once the message stopped being deliverable. ` +
          `Resending would send a blank email. Re-mint the invitation from the ` +
          `campaign's Directors panel instead.`;
      } else if (code === 409 && reason === "already_queued") {
        msg = `Email #${row.id} is already queued.`;
        tone = "warn";
      } else if (code === 409 && reason === "recipient_unsubscribed") {
        // marketing staff 2026-10: a newsletter copy is never re-queued to an address that no longer consents.
        msg = `Email #${row.id} was not resent: the recipient has unsubscribed from (or switched off) the newsletter.`;
        tone = "warn";
      } else if (code === 409 && reason === "in_flight") {
        msg = `Email #${row.id} is being sent right now. Check its status again in a minute.`;
        tone = "warn";
      } else if (code === 409 && reason === "row_changed") {
        // the row moved on (the mail queue picked it up) between loading this page and pressing Resend
        msg = `Email #${row.id} changed while you were looking at it. Reload the log and check its status before resending.`;
        tone = "warn";
      } else {
        msg = reason || err.message || "Resend failed";
        if (code === 409) tone = "warn";
      }
      setFlash({ tone, text: msg });
    } finally {
      setResendingId(null);
    }
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead
        title="Email log"
        subtitle={
          <>
            <span className="mono">{total}</span>
            <span>row{total === 1 ? "" : "s"}</span>
          </>
        }
      />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        Outbound mail queued for SMTP delivery. Failed and dead-letter rows can be re-queued; cron retries with a 0/5/15/60/240-minute backoff before giving up.
      </p>

      {flash && (
        <Alert tone={flash.tone} onClose={() => setFlash(null)}>
          {flash.text}
        </Alert>
      )}

      <section className="card">
        <div className="card__body">
          <form
            onSubmit={applyFilters}
            style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}
          >
            <div style={{ minWidth: 160 }}>
              <Field
                as="select"
                label="Status"
                name="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="0">Queued</option>
                <option value="1">Sent</option>
                <option value="2">Failed</option>
                <option value="3">Dead-letter</option>
              </Field>
            </div>
            <div style={{ minWidth: 220 }}>
              <Field
                label="Recipient contains"
                name="to"
                value={toFilter}
                onChange={(e) => setToFilter(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div style={{ minWidth: 180 }}>
              <Field
                label="Email type"
                name="emailType"
                value={emailType}
                onChange={(e) => setEmailType(e.target.value)}
                placeholder="EDUCATION_EMAIL"
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <Field
                label="From"
                name="from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <Field
                label="To"
                name="toDate"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <Btn variant="primary" type="submit">Apply</Btn>
            <Btn variant="ghost" type="button" onClick={resetFilters}>Reset</Btn>
          </form>
        </div>
      </section>

      {error && <Alert tone="danger">{error}</Alert>}

      {rows.length === 0 ? (
        <Empty>No email rows match these filters.</Empty>
      ) : (
        <section className="card">
          <div className="card__body card__body--flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>When</th>
                  <th>To</th>
                  <th>Subject</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Retries</th>
                  <th>Last attempt</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{formatAuditTimestamp(r.createdAt)}</td>
                    <td className="mono">{r.to || "—"}</td>
                    <td
                      className="muted"
                      title={r.subject || ""}
                      style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {r.subject || "—"}
                    </td>
                    <td className="muted">{r.emailType || "—"}</td>
                    <td><StatusPill status={r.status} /></td>
                    <td className="mono">{r.retry_count ?? 0}</td>
                    <td className="mono muted">
                      {r.last_attempt_at ? formatAuditTimestamp(r.last_attempt_at) : "—"}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <Btn size="sm" variant="ghost" onClick={() => openFull(r)}>View</Btn>
                      {(r.status === 2 || r.status === 3) && (
                        <Btn
                          size="sm"
                          variant="primary"
                          disabled={resendingId === r.id}
                          onClick={() => resend(r)}
                          style={{ marginLeft: 6 }}
                        >
                          {resendingId === r.id ? "..." : "Resend"}
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {totalPages > 1 && (
        <div className="toolbar">
          <Btn size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
            Page {page} of {totalPages}
          </span>
          <Btn size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}

      {openRow && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-log-dialog-title"
          ref={dialogRef}
          onClick={closeModal}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ maxWidth: 900, width: "100%", maxHeight: "90vh", overflow: "auto" }}
          >
            <div className="card__body">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div id="email-log-dialog-title" className="mono" style={{ fontSize: "var(--t-sm)", color: "var(--tx-3)" }}>
                    Email #{openRow.id} · {openRow.emailType || "—"} · {STATUS_LABELS[openRow.status] || openRow.status}
                  </div>
                  <div style={{ fontWeight: 600, marginTop: 4 }}>{openRow.subject || "(no subject)"}</div>
                  <div className="muted mono" style={{ fontSize: "var(--t-sm)" }}>
                    From {openRow.from || "—"} → {openRow.to || "—"}
                  </div>
                </div>
                <Btn size="sm" variant="ghost" onClick={closeModal}>Close</Btn>
              </div>
              {modalError && (
                <Alert tone="danger" style={{ marginBottom: 12 }}>{modalError}</Alert>
              )}
              {openRow.error_message && (
                <Alert tone="danger" style={{ marginBottom: 12 }}>
                  <strong>SMTP error:</strong>{" "}
                  <span className="mono" style={{ wordBreak: "break-all" }}>{openRow.error_message}</span>
                </Alert>
              )}
              {openLoading && <Loader isLoading={true} />}
              {openRow.body && (
                <details open>
                  <summary style={{ cursor: "pointer", marginBottom: 8 }}>Body (HTML source)</summary>
                  <pre
                    style={{
                      background: "var(--bg-2)", padding: 12, borderRadius: 6,
                      overflow: "auto", maxHeight: "50vh", fontSize: "var(--t-sm)",
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}
                  >
                    {openRow.body}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}

export default EmailLogViewer;
